import React, { Fragment } from "react";
import {
    useReactTable,
    getCoreRowModel,
    getExpandedRowModel,
    type ColumnDef,
    flexRender,
    type Row,
    type ColumnFiltersState,
    getFilteredRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getSortedRowModel,
} from "@tanstack/react-table";
import CmpFilter from "./cmp-filter";
import Loader from "../loader";

interface TableProps<TData> {
    data: TData[];
    headers: Array<ColumnDef<TData>>;
    renderSubComponent: (props: { row: Row<TData> }) => React.ReactElement;
    getRowCanExpand: (row: Row<TData>) => boolean;
    enableColumnFilter?: boolean;
    loading?: boolean;
    pageCount?: number;
    totalCount?: number;
    onPaginationChange?: any;
    pagination?: any;
    handleFilter?: any;
    pageSizeChangeAction?: any;
}

const CmpTable = <TData extends object>(props: TableProps<TData>) => {
    const {
        data,
        headers,
        renderSubComponent,
        getRowCanExpand,
        enableColumnFilter = true,
        loading = false,
        pageCount,
        onPaginationChange,
        pagination,
        handleFilter,
        pageSizeChangeAction,
    } = props;

    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

    const columns = [
        ...headers,
        {
            id: "expander",
            header: "Actions",
            cell: ({ row }: { row: Row<TData> }) => {
                return row.getCanExpand() ? (
                    <button
                        type="button"
                        onClick={() => {
                            table.toggleAllRowsExpanded(false);
                            row.toggleExpanded(!row.getIsExpanded());
                        }}
                        className="btn btn btn-primary btn-sm btn--angle text-nowrap"
                    >
                        More Option
                    </button>
                ) : (
                    ""
                );
            },
        },
    ];

    const table = useReactTable<TData>({
        data,
        columns,
        getRowCanExpand,
        pageCount,
        state: {
            columnFilters,
            pagination,
        },
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getSortedRowModel: getSortedRowModel(),
        onPaginationChange,
        manualPagination: true,
    });

    const handlePagination = (navigation: string) => {
        navigation === "prev" ? table.previousPage() : table.nextPage();
        table.setState((prevState) => {
            return {
                ...prevState,
                expanded: {},
            };
        });
    };

    const setPageSize = (pageSize: number, pageSizeChangeAction: () => void) => {
        table.setPageIndex(0);
        table.setPageSize(pageSize);
        pageSizeChangeAction();
    };

    return (
        <div className="cmp-table position-relative">
            {loading && <Loader />}

            <div className="table-responsive">
                <table className="table cmp-table__table mb-0">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <th key={header.id} colSpan={header.colSpan}>
                                            {header.isPlaceholder ? null : (
                                                <>
                                                    <div
                                                        {...{
                                                            className: header.column.getCanSort()
                                                                ? "table-heading cursor-pointer"
                                                                : "",
                                                            onClick:
                                                                header.column.getToggleSortingHandler(),
                                                        }}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                        {{
                                                            asc: (
                                                                <i className="fa fa-angle-up ml-1"></i>
                                                            ),
                                                            desc: (
                                                                <i className="fa fa-angle-down ml-1"></i>
                                                            ),
                                                        }[header.column.getIsSorted() as string] ??
                                                            null}
                                                    </div>
                                                    {header.column.getCanFilter() &&
                                                    enableColumnFilter ? (
                                                        <div className="table-filter">
                                                            <CmpFilter
                                                                columnName={header.column.id}
                                                                handleChange={(value: string) => {
                                                                    table.setPageIndex(0);
                                                                    handleFilter(
                                                                        value,
                                                                        header.column.id
                                                                    );
                                                                }}
                                                            />
                                                        </div>
                                                    ) : null}
                                                </>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>

                    <tbody>
                        {table.getRowModel().rows.length
                            ? table.getRowModel().rows.map((row) => {
                                  return (
                                      <Fragment key={row.id}>
                                          <tr>
                                              {row.getVisibleCells().map((cell) => {
                                                  return (
                                                      <td key={cell.id}>
                                                          {flexRender(
                                                              cell.column.columnDef.cell,
                                                              cell.getContext()
                                                          )}
                                                      </td>
                                                  );
                                              })}
                                          </tr>
                                          {row.getIsExpanded() && (
                                              <tr>
                                                  <td colSpan={row.getVisibleCells().length}>
                                                      {renderSubComponent({
                                                          row,
                                                      })}
                                                  </td>
                                              </tr>
                                          )}
                                      </Fragment>
                                  );
                              })
                            : !loading && (
                                  <tr>
                                      <td
                                          className="text-danger text-center"
                                          colSpan={table.getAllColumns().length}
                                      >
                                          No Row Found!!!
                                      </td>
                                  </tr>
                              )}
                    </tbody>
                </table>
            </div>

            <div className="cmp-table__pagination border p-1 rounded mt-4">
                <div className="row align-items-center">
                    <div className="col-2">
                        <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={() => {
                                handlePagination("prev");
                            }}
                            disabled={!table.getCanPreviousPage()}
                        >
                            {"<"} Previous
                        </button>
                    </div>
                    <div className="col-8 d-flex align-items-center justify-content-between">
                        <div className="cmp-table__pages">
                            Page{" "}
                            <strong>
                                {table.getPageCount()
                                    ? table.getState().pagination.pageIndex + 1
                                    : 1}{" "}
                                of {table.getPageCount() || 1}
                            </strong>
                        </div>

                        <div className="d-inline-flex align-items-center">
                            Show
                            <select
                                className="form-control mb-0 h-auto p-2 pr-0 ml-2"
                                value={table.getState().pagination.pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value), pageSizeChangeAction);
                                }}
                            >
                                {[10, 20, 30, 40, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="col-2">
                        <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={() => {
                                handlePagination("next");
                            }}
                            disabled={!table.getCanNextPage()}
                        >
                            Next {">"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CmpTable;
